import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '../config/api';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean | { requiresMFA: true; sessionToken: string }>;
  completeMFALogin: (sessionToken: string, totpCode?: string, backupCode?: string) => Promise<boolean>;
  logout: () => void;
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Configuration moved to config/api.ts

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    console.log('🔍 AuthContext useEffect - token found:', !!token);
    
    if (token) {
      console.log('🔍 Validating token with profile endpoint...');
      
      // Vérifier si le token est valide
      fetch(API_ENDPOINTS.PROFILE, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        console.log('🔍 Profile response status:', response.status);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('🔍 Profile response data:', data);
        
        if (data.success && data.data) {
          console.log('✅ Token valid, setting authenticated state');
          setIsAuthenticated(true);
          setUser(data.data);
        } else if (data.requiresMFA) {
          console.log('⚠️ MFA required, clearing auth state');
          // MFA requis, rediriger vers le login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setIsAuthenticated(false);
          setUser(null);
        } else {
          console.log('❌ Token invalid, removing from localStorage');
          // Token invalide, le supprimer
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      })
      .catch(error => {
        console.error('❌ Error verifying token:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean | { requiresMFA: true; sessionToken: string }> => {
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      // Gérer les erreurs HTTP spécifiques
      if (response.status === 429) {
        throw new Error('Trop de tentatives de connexion, veuillez réessayer dans 15 minutes');
      }

      if (data.success) {
        // Vérifier si MFA est requis
        if (data.data.requiresMFA) {
          return {
            requiresMFA: true,
            sessionToken: data.data.sessionToken
          };
        }

        const { user, accessToken, refreshToken } = data.data;
        
        // Stocker les tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        
        // Mettre à jour l'état
        setIsAuthenticated(true);
        setUser(user);
        
        return true;
      } else {
        console.error('Erreur de connexion:', data.error);
        throw new Error(data.error || 'Identifiants invalides');
      }
    } catch (error) {
      console.error('Erreur réseau:', error);
      throw error;
    }
  };

  const completeMFALogin = async (sessionToken: string, totpCode?: string, backupCode?: string): Promise<boolean> => {
    try {
      const response = await fetch(API_ENDPOINTS.COMPLETE_MFA_LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          sessionToken, 
          ...(totpCode && { totpCode }),
          ...(backupCode && { backupCode })
        }),
      });

      const data = await response.json();

      if (data.success) {
        const { user, accessToken, refreshToken } = data.data;
        
        // Stocker les tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        
        // Mettre à jour l'état
        setIsAuthenticated(true);
        setUser(user);
        
        return true;
      } else {
        console.error('Erreur completion MFA:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Erreur réseau MFA:', error);
      return false;
    }
  };

  const logout = () => {
    // Supprimer les tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    // Mettre à jour l'état
    setIsAuthenticated(false);
    setUser(null);
  };

  const value = {
    isAuthenticated,
    login,
    completeMFALogin,
    logout,
    user,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 